import { storeToRefs } from 'pinia'
import { computed, ComputedRef, Ref, ref, watch } from 'vue'

import { useCallSessionMediaStore } from '@/entities/call-session-media'
import { HTMLAudioElementWithSetSinkId, useSetAudioOutput, useSpeakerMediaDeviceStore, useStoreMediaDevices } from '@/entities/media-devices'

import { useWebRTC, STATE, RTCSessionFacade } from '@/shared/services/jssip'

interface Result {
  liveCallSessions: ComputedRef<RTCSessionFacade[]>
  processSessions: ComputedRef<RTCSessionFacade[]>
  pendingSessions: ComputedRef<RTCSessionFacade[]>
  player: Ref<HTMLAudioElementWithSetSinkId | undefined>
  currentSession: ComputedRef<RTCSessionFacade | undefined>
}

const player = ref<HTMLAudioElementWithSetSinkId>()

export const useLiveCallSessions = (): Result => {
  const { volume, isSpeakersMuted } = storeToRefs(useSpeakerMediaDeviceStore())
  const { activeSpeakerId } = storeToRefs(useStoreMediaDevices())
  const { sessions, activeSessionId } = useWebRTC()
  const { getAudioStreamsInSession } = useCallSessionMediaStore()

  const liveCallSessions = computed(() => {
    return Array.from(sessions.value.values())
  })

  const processSessions = computed(() => liveCallSessions.value.filter((session) => {
    const st = session.sessionState.value
    return st === STATE.CONNECTED || st === STATE.ONHOLD
  }))

  const pendingSessions = computed(() => liveCallSessions.value.filter((session) => {
    const st = session.sessionState.value
    return st === STATE.RINGING || st === STATE.PROGRESS
  }))

  const currentSession = computed(() => {
    if (!activeSessionId.value) return undefined
    return sessions.value.get(activeSessionId.value)
  })

  const currentAudioStream = computed((): MediaStream | undefined => {
    let tracks
    if (currentSession.value?.sessionId) {
      tracks = getAudioStreamsInSession(currentSession.value?.sessionId)?.flatMap(x => x.getAudioTracks())
    }
    if (!tracks || !tracks?.length) return undefined
    return new MediaStream(tracks)
  })
  watch([currentAudioStream, player], ([stream, newPlayer]) => {
    if (!newPlayer || !stream) return

    newPlayer.srcObject = stream
    newPlayer.addEventListener('loadeddata', () => {
      newPlayer.play().catch((e) => {
        console.warn(e)
      })
    })
  })

  watch([volume, player], ([newVolume]) => {
    if (player.value) {
      player.value.volume = newVolume
    }
  })

  watch([isSpeakersMuted, player], ([newSpeakersMutedValue]) => {
    if (player.value) {
      player.value.muted = newSpeakersMutedValue
    }
  })

  watch([player, activeSpeakerId], ([newPlayer, newActiveSpeakerId]) => {
    if (newPlayer) {
      useSetAudioOutput(newPlayer, newActiveSpeakerId).catch((error: unknown) => {
        console.warn(error)
      })
    }
  })

  watch(activeSessionId, (n, o) => {
    if (n && !o && player.value) {
      useSetAudioOutput(player.value, activeSpeakerId.value).catch((error: unknown) => {
        console.warn(error)
      })
    }
  })

  return {
    liveCallSessions,
    processSessions,
    pendingSessions,
    player,
    currentSession,
  }
}
